/**
 * UserManagementPage — tenant users listing + invite + role change + deactivation.
 *
 * Visible to anyone with the `user:list` permission (Owner / Admin / Platform Root).
 * Uses the invite-token flow (POST /api/users/invites) so new users set their own
 * password.
 *
 * Iframe-safe: no window.confirm / window.alert. Inline confirmation used for
 * destructive actions.
 */
import React, { useEffect, useState } from 'react';
import AppHeader from './layout/AppHeader';
import { useAuth } from '../context/AuthContext';
import GlassSelect from './GlassSelect';
import EditAccessModal from './EditAccessModal';
import { formatBackendDetail } from '../lib/apiErrors';
import { apiErrorFromResponse, messageFor } from '../lib/apiErrors';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: 'sales_demo', label: 'Sales Demo (dedicated workspace only)' },
  { value: 'viewer',     label: 'Viewer' },
  { value: 'user',       label: 'Operator' },
  { value: 'admin',      label: 'Admin' },
  { value: 'super_user', label: 'Owner' },
];

function Badge({ tone = 'default', children, testid }) {
  const palette = {
    default: { bg: '#f3f4f6', color: '#374151' },
    success: { bg: '#dcfce7', color: '#166534' },
    warn:    { bg: '#fef3c7', color: '#92400e' },
    danger:  { bg: '#fee2e2', color: '#991b1b' },
  }[tone];
  return (
    <span data-testid={testid} style={{
      fontSize: 10, padding: '2px 6px', borderRadius: 10, textTransform: 'uppercase',
      letterSpacing: '0.05em', fontWeight: 600, background: palette.bg, color: palette.color,
    }}>{children}</span>
  );
}

function InviteDialog({ onClose, onInvited }) {
  const [form, setForm] = useState({ email: '', name: '', role: 'user' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${API}/api/users/invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email.trim().toLowerCase(),
          name: form.name.trim(),
          role: form.role,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(formatBackendDetail(d.detail) || `HTTP ${res.status}`);
      }
      const invite = await res.json();
      setResult(invite);
      onInvited();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const acceptUrl = result ? `${window.location.origin}${result.accept_url}` : '';

  return (
    <div className="panel" style={{ marginBottom: 16 }} data-testid="invite-dialog">
      <div className="panel-header">Invite a new user</div>
      <div className="panel-body">
        {!result && (
          <form onSubmit={submit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Email *
              <input type="email" required value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     data-testid="invite-email"
                     style={{ width: '100%', padding: 6, marginTop: 2, border: '1px solid var(--border)', borderRadius: 4 }} />
            </label>
            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Name *
              <input type="text" required value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="invite-name"
                     style={{ width: '100%', padding: 6, marginTop: 2, border: '1px solid var(--border)', borderRadius: 4 }} />
            </label>
            <label style={{ gridColumn: 'span 2', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Role *
              <GlassSelect value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}
                      data-testid="invite-role"
                      style={{ width: '100%', padding: 6, marginTop: 2, border: '1px solid var(--border)', borderRadius: 4 }}>
                {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </GlassSelect>
            </label>
            {error && <div className="auth-error" style={{ gridColumn: 'span 2' }} data-testid="invite-error">{error}</div>}
            <div style={{ gridColumn: 'span 2', display: 'flex', gap: 8 }}>
              <button type="submit" className="btn btn-primary btn-sm" disabled={busy}
                      data-testid="invite-submit">
                {busy ? 'Creating…' : 'Create invite'}
              </button>
              <button type="button" className="btn btn-outline btn-sm" onClick={onClose}
                      data-testid="invite-cancel">Cancel</button>
            </div>
          </form>
        )}
        {result && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="invite-success">
            <div style={{ fontSize: 13 }}>
              Invite created for <strong>{result.email}</strong> as <strong>{result.role_label}</strong>.
              Share this link — it expires {new Date(result.expires_at).toLocaleDateString()}.
            </div>
            <div style={{
              padding: 8, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4,
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, wordBreak: 'break-all',
            }} data-testid="invite-accept-url">{acceptUrl}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-outline btn-sm"
                      onClick={() => navigator.clipboard?.writeText(acceptUrl)}
                      data-testid="invite-copy-btn">Copy link</button>
              <button className="btn btn-outline btn-sm" onClick={onClose}
                      data-testid="invite-done-btn">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RoleSelect({ value, disabled, onChange, testid }) {
  return (
    <GlassSelect value={value} disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testid}
            style={{ padding: 4, fontSize: 12, border: '1px solid var(--border)', borderRadius: 4 }}>
      {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
    </GlassSelect>
  );
}

export default function UserManagementPage() {
  const { user: me, hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeDeactivated, setIncludeDeactivated] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [pendingDeactivate, setPendingDeactivate] = useState(null);
  const [editingAccess, setEditingAccess] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const canManage = hasPermission('user:invite') || hasPermission('user:delete');

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API}/api/users/users?include_deactivated=${includeDeactivated}`);
      if (!res.ok) throw await apiErrorFromResponse(res, { context: 'users' });
      setUsers(await res.json());
    } catch (e) { setError(messageFor(e, { context: 'users' })); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [includeDeactivated]);

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    setBusyId(u.id); setError(null);
    try {
      const res = await fetch(`${API}/api/users/users/${u.id}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(formatBackendDetail(d.detail) || `HTTP ${res.status}`);
      }
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusyId(null); }
  };

  const doDeactivate = async (u) => {
    setBusyId(u.id); setError(null);
    try {
      const res = await fetch(`${API}/api/users/users/${u.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(formatBackendDetail(d.detail) || `HTTP ${res.status}`);
      }
      setPendingDeactivate(null);
      await load();
    } catch (e) { setError(e.message); setPendingDeactivate(null); }
    finally { setBusyId(null); }
  };

  const reactivate = async (u) => {
    setBusyId(u.id); setError(null);
    try {
      const res = await fetch(`${API}/api/users/users/${u.id}/reactivate`, { method: 'POST' });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(formatBackendDetail(d.detail) || `HTTP ${res.status}`);
      }
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusyId(null); }
  };

  return (
    <>
      <AppHeader active="users" />
      <div className="page-shell" data-testid="user-management-page">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: '8px 0 2px' }}>Users</h2>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
              Invite teammates, change roles, and deactivate access. Deactivated users can be
              reactivated at any time — audit trail preserved.
            </p>
          </div>
          {canManage && (
            <button className="btn btn-primary btn-sm"
                    onClick={() => setShowInvite(true)}
                    data-testid="user-invite-btn">+ Invite user</button>
          )}
        </div>

        {showInvite && (
          <InviteDialog onClose={() => setShowInvite(false)} onInvited={load} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={includeDeactivated}
                   onChange={(e) => setIncludeDeactivated(e.target.checked)}
                   data-testid="user-include-deactivated" />
            Show deactivated users
          </label>
          <button className="btn btn-outline btn-sm" onClick={load} data-testid="user-refresh">
            Refresh
          </button>
        </div>

        {error && <div className="auth-error" data-testid="user-error">{error}</div>}

        <div className="panel">
          <div className="panel-body" style={{ padding: 0 }}>
            <div className="data-table-wrap">
              <table className="data-table" style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Access profile</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 16, fontSize: 12, color: 'var(--text-secondary)' }}>
                      Loading users…
                    </td></tr>
                  )}
                  {!loading && users.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 16, fontSize: 12, color: 'var(--text-secondary)' }}>
                      No users match.
                    </td></tr>
                  )}
                  {!loading && users.map((u) => {
                    const isMe = u.id === me?.id;
                    const isDeactivated = !!u.deactivated;
                    const busy = busyId === u.id;
                    return (
                      <tr key={u.id} data-testid={`user-row-${u.id}`}
                          style={{ opacity: isDeactivated ? 0.6 : 1 }}>
                        <td style={{ fontWeight: 600, fontSize: 13 }}>
                          {u.name} {isMe && <Badge tone="default" testid={`user-you-${u.id}`}>YOU</Badge>}
                        </td>
                        <td style={{ fontSize: 12 }}>{u.email}</td>
                        <td>
                          {canManage && !isDeactivated && !isMe ? (
                            <RoleSelect value={u.role} disabled={busy}
                                        onChange={(r) => changeRole(u, r)}
                                        testid={`user-role-select-${u.id}`} />
                          ) : (
                            <span style={{ fontSize: 12 }}>{u.role_label}</span>
                          )}
                        </td>
                        <td>
                          <span data-testid={`user-access-profile-${u.id}`}
                                style={{ fontSize: 12, padding: '2px 8px', borderRadius: 999,
                                         background: 'rgba(186,151,123,0.15)',
                                         color: '#7c5a40', fontWeight: 500 }}>
                            {(u.user_access_profile || 'viewer').replace(/_/g, ' ')
                              .replace(/\b\w/g, (c) => c.toUpperCase())}
                          </span>
                        </td>
                        <td>
                          {isDeactivated
                            ? <Badge tone="danger" testid={`user-status-${u.id}`}>Deactivated</Badge>
                            : <Badge tone="success" testid={`user-status-${u.id}`}>Active</Badge>}
                        </td>
                        <td style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {canManage && !isMe && !isDeactivated && (
                            <button className="btn btn-outline btn-sm" disabled={busy}
                                    onClick={() => setEditingAccess(u)}
                                    style={{ marginRight: 6 }}
                                    data-testid={`user-edit-access-${u.id}`}>
                              Edit access
                            </button>
                          )}
                          {canManage && !isMe && (
                            isDeactivated ? (
                              <button className="btn btn-outline btn-sm" disabled={busy}
                                      onClick={() => reactivate(u)}
                                      data-testid={`user-reactivate-${u.id}`}>
                                {busy ? '…' : 'Reactivate'}
                              </button>
                            ) : (
                              <button className="btn btn-outline btn-sm" disabled={busy}
                                      onClick={() => setPendingDeactivate(u)}
                                      style={{ color: 'var(--danger)' }}
                                      data-testid={`user-deactivate-${u.id}`}>
                                Deactivate
                              </button>
                            )
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {editingAccess && (
          <EditAccessModal
            user={editingAccess}
            onClose={() => setEditingAccess(null)}
            onSaved={() => { setEditingAccess(null); load(); }}
          />
        )}

        {/* Inline deactivation confirm — iframe-safe */}
        {pendingDeactivate && (
          <div className="panel" style={{ marginTop: 12, borderColor: 'var(--danger)' }}
               data-testid="deactivate-confirm">
            <div className="panel-body" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13 }}>
                Deactivate <strong>{pendingDeactivate.name}</strong> ({pendingDeactivate.email})?
                Their sessions will be revoked immediately. You can reactivate them later.
              </span>
              <button className="btn btn-primary btn-sm" style={{ background: 'var(--danger)' }}
                      disabled={busyId === pendingDeactivate.id}
                      onClick={() => doDeactivate(pendingDeactivate)}
                      data-testid="deactivate-confirm-btn">
                {busyId === pendingDeactivate.id ? 'Deactivating…' : 'Yes, deactivate'}
              </button>
              <button className="btn btn-outline btn-sm"
                      onClick={() => setPendingDeactivate(null)}
                      data-testid="deactivate-cancel-btn">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
